import axios from 'axios';
import i18n, { resetLocale } from '../i18n';
import profileService from './profileService';
import {
  saveAuthToken,
  saveRefreshToken,
  saveUserData,
  getAuthToken,
  getRefreshToken,
  clearAuthData
} from '../utils/storage';
import { API_BASE_URL, APP_CONFIG } from '../utils/constants';
import { extractApiError } from '../utils/apiError';
import { clearSessionCaches } from '../utils/sessionCaches';

/**
 * Axios instance configured for authentication-related requests
 * - Sets baseURL from project constants and default json headers
 * - Uses APP_CONFIG.REQUEST_TIMEOUT for request timeout
 */
// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: APP_CONFIG.REQUEST_TIMEOUT,
});

// Request interceptor to add auth token to requests
/**
 * Attach stored auth token to outgoing requests, when available.
 * - Uses `getAuthToken()` to read the token from storage utilities.
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and errors
/**
 * Intercepts responses to handle 401 Unauthorized errors by attempting
 * a token refresh using the refresh token, retrying the original request
 * with a new access token when successful. If refresh fails, clears
 * auth data and redirects to the login page.
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // A 401 from the auth endpoints themselves means "wrong credentials", not
    // "your session expired" — so it must never trigger the refresh-and-redirect
    // path below. Redirecting there would reload the page out from under the
    // login form and take the error message with it, which is exactly the bug
    // this guard prevents. (Dormant today: the backend issues no refresh token
    // and has no /auth/refresh route, so the block below is skipped for want of
    // a token. It stops being dormant the moment either is added.)
    const requestUrl = originalRequest?.url || '';
    const isAuthEndpoint = requestUrl.includes('/auth/login')
      || requestUrl.includes('/auth/refresh');

    // If the error is 401 (Unauthorized) and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          
          // Save new tokens
          saveAuthToken(accessToken);
          if (newRefreshToken) {
            saveRefreshToken(newRefreshToken);
          }

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear all auth data and send the user to sign in again.
        // Skip the navigation when already on /login: reloading that page would
        // discard whatever the form is currently showing for no benefit.
        clearAuthData();
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Authentication service class
 */
class AuthService {
  /**
   * Decode JWT token to extract user information
   * @param {string} token - JWT token
   * @returns {object|null} User data from token or null if invalid
   */
  decodeJWTUser(token) {
    try {
      if (!token) return null;
      
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      
      // Extract user information from JWT payload
      return {
        id: decoded.user_id,
        email: decoded.sub,
        role: decoded.user_role,
        // Add other fields as needed
      };
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {boolean} rememberMe - Whether to persist login (localStorage vs sessionStorage)
   * @returns {Promise<object>} Login response with user data and tokens
   */
  async login(identifier, password, rememberMe = false) {
    try {
      // Create form data for application/x-www-form-urlencoded
      const formData = new URLSearchParams();
      formData.append('username', identifier);
      formData.append('password', password);
      
      const response = await apiClient.post('/api/v1/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      });
      
      // Handle different response formats from backend
      let user, access_token, refresh_token;
      
      if (response.data.user) {
        // Backend sends user data directly
        ({ user, access_token, refresh_token } = response.data);
      } else if (response.data.access_token) {
        // Backend only sends tokens, decode JWT for user info
        access_token = response.data.access_token;
        refresh_token = response.data.refresh_token;
        
        // Decode JWT to extract user information
        user = this.decodeJWTUser(access_token);
      } else {
        // Fallback to old naming convention
        access_token = response.data.accessToken || response.data.access_token;
        refresh_token = response.data.refreshToken || response.data.refresh_token;
        user = response.data.user || this.decodeJWTUser(access_token);
      }

      // Store tokens and user data based on rememberMe preference
      saveAuthToken(access_token, rememberMe);
      saveUserData(user, rememberMe);
      
      if (refresh_token) {
        saveRefreshToken(refresh_token, rememberMe);
      }

      return {
        success: true,
        user,
        accessToken: access_token,
        refreshToken: refresh_token,
      };
    } catch (error) {
      console.error('Login error:', error);
      
      // Extract error message from response
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Register new user
   * @param {object} userData - User registration data
   * @returns {Promise<object>} Registration response
   */
  async register(userData) {
    try {
      const response = await apiClient.post('/api/v1/auth/register', userData);

      const { user, accessToken, refreshToken } = response.data;

      // Store tokens and user data
      saveAuthToken(accessToken);
      saveUserData(user);
      
      if (refreshToken) {
        saveRefreshToken(refreshToken);
      }

      return {
        success: true,
        user,
        accessToken,
        refreshToken,
      };
    } catch (error) {
      console.error('Registration error:', error);
      
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Logout user
   * @returns {Promise<object>} Logout response
   */
  async logout() {
    try {
      const refreshToken = getRefreshToken();
      
      // Call logout endpoint if refresh token exists
      if (refreshToken) {
        await apiClient.post('/api/v1/auth/logout', {
          refreshToken,
        });
      }

      // Clear all stored auth data
      clearAuthData();
      // Clear per-user caches/language so the next user starts clean.
      try { profileService.clearCache(); } catch (e) { /* ignore */ }
      try { clearSessionCaches(); } catch (e) { /* ignore */ }
      try { resetLocale(); } catch (e) { /* ignore */ }

      return {
        success: true,
      };
    } catch (error) {
      console.error('Logout error:', error);

      // Clear auth data even if logout API call fails
      clearAuthData();
      try { profileService.clearCache(); } catch (e) { /* ignore */ }
      try { clearSessionCaches(); } catch (e) { /* ignore */ }
      try { resetLocale(); } catch (e) { /* ignore */ }

      return {
        success: true, // Still consider it successful since we cleared local data
      };
    }
  }

  /**
   * Validate if the current stored token is valid
   * @returns {boolean} True if token exists and is not expired
   */
  isTokenValid() {
    try {
      const token = getAuthToken();
      if (!token) {
        return false;
      }

      // Decode JWT to check expiration
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      
      // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
      const currentTime = Math.floor(Date.now() / 1000);
      const tokenExpiration = decoded.exp;
      
      if (tokenExpiration && tokenExpiration < currentTime) {
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated (locally, without API call)
   * @returns {object} Authentication status and user data
   */
  checkAuthStatus() {
    try {
      const token = getAuthToken();
      if (!token) {
        return {
          isAuthenticated: false,
          user: null,
          needsRefresh: false
        };
      }

      if (!this.isTokenValid()) {
        return {
          isAuthenticated: false,
          user: null,
          needsRefresh: false
        };
      }

      // Get stored user data
      const userData = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
      let user = null;
      
      if (userData) {
        user = JSON.parse(userData);
      } else {
        // Fallback: decode user from JWT
        user = this.decodeJWTUser(token);
      }

      // Ensure the user always carries a role. Stored user_data may predate the
      // role field (or omit it), which would leave the app stuck on "Loading
      // session..." because role context never resolves. Backfill from the JWT.
      if (user && !user.role) {
        const decoded = this.decodeJWTUser(token);
        if (decoded?.role) {
          user = { ...user, role: decoded.role };
        }
      }

      return {
        isAuthenticated: true,
        user,
        needsRefresh: false
      };
    } catch (error) {
      console.error('Error checking auth status:', error);
      return {
        isAuthenticated: false,
        user: null,
        needsRefresh: false
      };
    }
  }


  /**
   * Change the signed-in user's own password. Same route the Profile page uses
   * (POST /auth/reset-password: current password verified server-side). The old
   * PUT /auth/change-password this called never existed on the backend.
   */
  async updatePassword(currentPassword, newPassword) {
    try {
      const response = await apiClient.post('/api/v1/auth/reset-password', {
        old_password: currentPassword,
        new_password: newPassword,
        confirm_password: newPassword,
      });
      return { success: true, data: response.data, message: response.data?.message || 'Password updated successfully' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message || 'Failed to update password' };
    }
  }

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<object>} Password reset response
   */
  async requestPasswordReset(identifier) {
    try {
      const response = await apiClient.post('/api/v1/auth/forgot-password', {
        email: identifier,
      });

      return {
        success: true,
        message: response.data.message || i18n.t('serviceMessages.auth.passwordResetEmailSent'),
      };
    } catch (error) {
      console.error('Password reset request error:', error);
      
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<object>} Password reset response
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await apiClient.post('/api/v1/auth/reset-password', {
        token,
        newPassword,
      });

      return {
        success: true,
        message: response.data.message || i18n.t('serviceMessages.auth.passwordReset'),
      };
    } catch (error) {
      console.error('Password reset error:', error);
      
      const errorMessage = extractApiError(error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}

// Export a singleton instance of AuthService and the configured apiClient
const authService = new AuthService();
export default authService;

// Also export the apiClient for other services to use (e.g., to share interceptors)
export { apiClient };
